import { useCallback, useState } from "react"
import { Account } from "../types/types"
import { apiFetchProtected, apiFetch } from "../utils/api"
import { useAccountStore } from "../utils/store"

export enum AuthStatus {
  Unknown = 0,
  Authenticated = 1,
  Guest = 2
}

export const useAuth = () => {
  const { account, setAccount } = useAccountStore()
  const [resetPasswordRequested, setResetPasswordRequested] = useState(false)
  const [passwordResetted, setPasswordResetted] = useState(false)
  const [loginError, setLoginError] = useState(false)
  const [loading, setLoading] = useState(false)

  let status
  switch (account) {
    case null:
      status = AuthStatus.Guest
      break
    case undefined:
      status = AuthStatus.Unknown
      break
    default:
      status = AuthStatus.Authenticated
      break
  }

  const authenticate = useCallback(() => {
    apiFetchProtected<Account>("/users/me", {})
      .then(setAccount)
      .catch(() => setAccount(null))
  }, [setAccount])

  const login = useCallback(
    (username: string, password: string) => {
      setLoading(true)
      apiFetch<Account>("/users/sign_in", {
        body: { user: { email: username, password } }
      })
        .then((account) => {
          setAccount(account)
          setLoading(false)
        })
        .catch(() => {
          setLoginError(true)
          setLoading(false)
        })
    },
    [setAccount]
  )

  const requestPasswordReset = useCallback((email: string) => {
    setLoading(true)
    apiFetch("/users/password", {
      body: { user: { email } }
    })
      .then(() => {
        setResetPasswordRequested(true)
        setLoading(false)
      })
      .catch(() => {
        setResetPasswordRequested(false)
        setLoading(false)
      })
  }, [])

  const resetPassword = useCallback(
    (password: string, reset_password_token: string | null) => {
      setLoading(true)
      apiFetch("/users/password", {
        body: { user: { password, reset_password_token } },
        method: "PATCH"
      })
        .then(() => {
          setPasswordResetted(true)
          setLoading(false)
        })
        .catch(() => {
          setPasswordResetted(false)
          setLoading(false)
        })
    },
    []
  )

  const logout = useCallback(() => {
    apiFetch<Account>("/users/sign_out", { method: "DELETE" }).then(() =>
      setAccount(null)
    )
  }, [setAccount])

  const resetRequestPasswordReset = () => setResetPasswordRequested(false)

  return {
    status,
    resetPasswordRequested,
    passwordResetted,
    loginError,
    loading,
    resetRequestPasswordReset,
    resetPassword,
    requestPasswordReset,
    setLoginError,
    authenticate,
    login,
    logout
  }
}
